import React, { useState, useEffect, useCallback } from 'react';
import { todosAPI } from '../services/api';
import stateManager from '../services/stateManager';
import Todo from './Todo';
import TodoCompletionModal from './TodoCompletionModal';
import LoadingScreen from './LoadingScreen';
import { WarningIcon } from './Icons';
import styles from '../styles/TodoList.module.css';

function TodoList({ conversationId, onBackToChat, onChangePlan }) {
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStages, setExpandedStages] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'todo'|'stage', id|tierNumber, name }

  // Consolidated editing state to reduce re-renders
  const [editing, setEditing] = useState({
    type: null, // 'todo' | 'stage' | null
    id: null, // todoId or tierNumber
    text: '' // editing text value
  });

  // Check if all required todos are complete across all stages
  const areAllTodosComplete = (stagesData) => {
    if (!stagesData || stagesData.length === 0) return false;

    // Flatten all todos from all stages
    const allTodos = stagesData.flatMap(stage => stage.todos || []);

    // Get all non-deleted required todos
    const requiredTodos = allTodos.filter(todo => !todo.is_optional && !todo.is_deleted);

    // Check if all required todos are completed
    return requiredTodos.length > 0 && requiredTodos.every(todo => todo.is_completed);
  };

  // Calculate current stage (lowest stage with incomplete required todos)
  const getCurrentStageNumber = (stagesData) => {
    // Find first stage with incomplete required todos
    for (const stage of stagesData) {
      const hasIncompleteRequired = stage.todos.some(
        todo => !todo.is_optional && !todo.is_completed && !todo.is_deleted
      );
      if (hasIncompleteRequired) {
        return stage.tier_number;
      }
    }

    // If we get here, either all required todos are complete OR there are no stages
    // Verify all required todos are actually complete
    if (stagesData.length > 0) {
      const allComplete = areAllTodosComplete(stagesData);
      if (allComplete) {
        // Truly complete - return last stage
        return stagesData[stagesData.length - 1].tier_number;
      } else {
        // There are incomplete required todos somewhere - find the first stage with any todos
        for (const stage of stagesData) {
          if (stage.todos.length > 0) {
            return stage.tier_number;
          }
        }
        return 1; // Fallback
      }
    }

    return 1;
  };

  const loadTodos = useCallback(async () => {
    try {
      setError(null);

      // Check cache first BEFORE setting loading state
      const cachedTodos = stateManager.getTodos();
      if (cachedTodos) {
        // console.log('[TodoList] Using cached todos');
        setStages(cachedTodos);

        // Auto-expand current stage
        if (cachedTodos.length > 0) {
          const currentStageNum = getCurrentStageNumber(cachedTodos);
          setExpandedStages(new Set([currentStageNum]));
        }

        // No loading state needed - instant display
        setIsLoading(false);
        return;
      }

      // Only set loading if we need to fetch
      setIsLoading(true);

      // Fetch if cache miss or expired
      const data = await todosAPI.getTodos(conversationId);
      stateManager.setTodos(data);
      setStages(data);

      // Auto-expand current stage
      if (data.length > 0) {
        const currentStageNum = getCurrentStageNumber(data);
        setExpandedStages(new Set([currentStageNum]));
      }
    } catch (err) {
      console.error('Failed to load todos:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) {
      loadTodos();
    }
  }, [conversationId, loadTodos]);

  // Subscribe to todos changes from singleton
  useEffect(() => {
    const unsubscribe = stateManager.subscribe('todos', (newTodos) => {
      // console.log('[TodoList] Received todos update from singleton');
      setStages(newTodos);
    });

    return unsubscribe;
  }, []);

  // Listen for todosRefreshed custom event (from add/edit/remove tools)
  useEffect(() => {
    const handleTodosRefreshed = (event) => {
      console.log('[TodoList] Todos refreshed via custom event - updating UI');
      if (event.detail) {
        setStages(event.detail);
      }
    };

    window.addEventListener('todosRefreshed', handleTodosRefreshed);
    return () => window.removeEventListener('todosRefreshed', handleTodosRefreshed);
  }, []);

  // Scroll to top when stages load
  useEffect(() => {
    if (stages.length > 0) {
      // Find the stages list element and scroll to top
      const stagesList = document.querySelector(`.${styles.stagesList}`);
      if (stagesList) {
        stagesList.scrollTop = 0;
      }
    }
  }, [stages.length]);

  const currentStage = getCurrentStageNumber(stages);

  // Calculate progress for a stage (count only required/non-optional todos)
  const getStageProgress = (stage) => {
    const requiredTodos = stage.todos.filter(todo => !todo.is_deleted && !todo.is_optional);
    const completedRequiredTodos = requiredTodos.filter(todo => todo.is_completed).length;
    return {
      completed: completedRequiredTodos,
      total: requiredTodos.length
    };
  };

  // Check if stage is completed
  const isCompleted = (stageNumber) => {
    return stageNumber < currentStage;
  };

  const toggleStageExpanded = (stageNumber) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageNumber)) {
      newExpanded.delete(stageNumber);
    } else {
      newExpanded.add(stageNumber);
    }
    setExpandedStages(newExpanded);
  };

  const handleEditTodo = (todoId, currentText) => {
    setEditing({ type: 'todo', id: todoId, text: currentText });
  };

  const handleSaveTodoEdit = async () => {
    if (!editing.text.trim()) {
      setEditing({ type: null, id: null, text: '' });
      return;
    }

    const todoId = editing.id;
    const newText = editing.text;

    // Optimistic update - update UI immediately
    const updatedStages = stages.map(stage => ({
      ...stage,
      todos: stage.todos.map(todo =>
        todo.id === todoId ? { ...todo, todo_text: newText } : todo
      )
    }));
    setStages(updatedStages);
    stateManager.setTodos(updatedStages);
    setEditing({ type: null, id: null, text: '' });

    // Fire and forget - sync with server in background
    todosAPI.updateTodo(todoId, newText)
      .catch(err => {
        console.error('Failed to update todo:', err);
        setError('Your changes may not have been saved. Please try again.');
        setTimeout(() => setError(null), 4000);
        // Reload to get correct state from server
        loadTodos();
      });
  };

  const handleCancelTodoEdit = () => {
    setEditing({ type: null, id: null, text: '' });
  };

  const handleDeleteTodo = async (todoId) => {
    // Optimistic update - remove from UI immediately
    const updatedStages = stages.map(stage => ({
      ...stage,
      todos: stage.todos.filter(todo => todo.id !== todoId)
    }));
    setStages(updatedStages);
    stateManager.setTodos(updatedStages);
    setDeleteConfirm(null);

    // Check if all todos are now complete (after deletion)
    if (areAllTodosComplete(updatedStages)) {
      setNotification('all_complete');
    }

    // Fire and forget - sync with server in background
    todosAPI.deleteTodo(todoId)
      .catch(err => {
        console.error('Failed to delete todo:', err);
        setError('Your changes may not have been saved. Please try again.');
        setTimeout(() => setError(null), 4000);
        // Reload to get correct state from server
        loadTodos();
      });
  };

  const handleEditStage = (tierNumber, currentTitle) => {
    setEditing({ type: 'stage', id: tierNumber, text: currentTitle });
  };

  const handleSaveStageEdit = async () => {
    if (!editing.text.trim()) {
      setEditing({ type: null, id: null, text: '' });
      return;
    }

    const tierNumber = editing.id;
    const newTitle = editing.text;

    // Optimistic update - update UI immediately
    const updatedStages = stages.map(stage =>
      stage.tier_number === tierNumber ? { ...stage, tier_title: newTitle } : stage
    );
    setStages(updatedStages);
    stateManager.setTodos(updatedStages);
    setEditing({ type: null, id: null, text: '' });

    // Fire and forget - sync with server in background
    todosAPI.updateStage(conversationId, tierNumber, newTitle)
      .catch(err => {
        console.error('Failed to update stage:', err);
        setError('Your changes may not have been saved. Please try again.');
        setTimeout(() => setError(null), 4000);
        // Reload to get correct state from server
        loadTodos();
      });
  };

  const handleCancelStageEdit = () => {
    setEditing({ type: null, id: null, text: '' });
  };

  const handleDeleteStage = async (tierNumber) => {
    // Optimistic update - remove from UI immediately
    const updatedStages = stages.filter(stage => stage.tier_number !== tierNumber);
    setStages(updatedStages);
    stateManager.setTodos(updatedStages);
    setDeleteConfirm(null);

    // Fire and forget - sync with server in background
    todosAPI.deleteStage(conversationId, tierNumber)
      .catch(err => {
        console.error('Failed to delete stage:', err);
        setError('Your changes may not have been saved. Please try again.');
        setTimeout(() => setError(null), 4000);
        // Reload to get correct state from server
        loadTodos();
      });
  };

  const handleToggleTodo = async ({ todoId, onComplete, onError }) => {
    try {
      // console.log('[TodoList] Toggling todo:', todoId);

      // Make API call
      await todosAPI.toggleTodo(todoId);
      // console.log('[TodoList] API call successful');

      // Refresh todos from server to get actual state
      const freshTodos = await todosAPI.getTodos(conversationId);
      // console.log('[TodoList] Refreshed todos from server');

      // Update singleton cache
      stateManager.setTodos(freshTodos);

      // Update local state
      setStages(freshTodos);

      // Check if this completion unlocks a stage
      const completedTodoStage = freshTodos.find(stage =>
        stage.todos.some(todo => todo.id === todoId)
      );

      if (completedTodoStage) {
        const todo = completedTodoStage.todos.find(t => t.id === todoId);
        const currentStageNum = getCurrentStageNumber(freshTodos);

        // Only celebrate if completing (not uncompleting) the CURRENT stage
        if (todo && todo.is_completed && completedTodoStage.tier_number === currentStageNum) {
          // Check if all required todos in this stage are now complete
          const allRequiredComplete = completedTodoStage.todos
            .filter(t => !t.is_optional)
            .every(t => t.is_completed);

          if (allRequiredComplete) {
            // Find next stage
            const nextStage = freshTodos.find(s => s.tier_number === currentStageNum + 1);

            if (nextStage) {
              // Show celebration
              setNotification('stage_unlock');

              // Auto-expand the newly unlocked stage
              setExpandedStages(new Set([nextStage.tier_number]));
            } else {
              // No next stage - ALL STAGES COMPLETE!
              setNotification('all_complete');
            }
          }
        }
      }

      // Success - clear loading state
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('[TodoList] Failed to toggle todo:', err);
      setError(err.message || 'Failed to update todo');
      setTimeout(() => setError(null), 3000);

      // Notify error
      if (onError) {
        onError(err.message || 'Failed to update todo');
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBackToChat}>
            ← Back to Chat
          </button>
          <h1 className={styles.title}>Action Plan</h1>
          <button className={styles.changePlanButton} onClick={onChangePlan}>
            Change my action plan
          </button>
        </div>
        <LoadingScreen text="Loading your action plan..." variant="dots" showImage={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBackToChat}>
            ← Back to Chat
          </button>
          <h1 className={styles.title}>Action Plan</h1>
          <button className={styles.changePlanButton} onClick={onChangePlan}>
            Change my action plan
          </button>
        </div>
        <div className={styles.error}>Failed to load todos: {error}</div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBackToChat}>
            ← Back to Chat
          </button>
          <h1 className={styles.title}>Action Plan</h1>
          <button className={styles.changePlanButton} onClick={onChangePlan}>
            Change my action plan
          </button>
        </div>
        <div className={styles.emptyState}>
          <p>No action plan yet.</p>
          <p>Complete onboarding to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBackToChat}>
          ← Back to Chat
        </button>
        <h1 className={styles.title}>Action Plan</h1>
        <button className={styles.changePlanButton} onClick={onChangePlan}>
          Change my action plan
        </button>
        <div className={styles.overallProgress}>
          Stage {currentStage} of {stages.length} active
        </div>
      </div>

      {notification === 'stage_unlock' && (
        <div className={styles.modalOverlay} onClick={() => setNotification(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalContent}>
              <div className={styles.modalIcon}>🎉</div>
              <h2 className={styles.modalTitle}>New Stage Unlocked!</h2>
              <p className={styles.modalText}>
                Great progress! You've completed the previous stage and unlocked the next one.
              </p>
              <button
                className={styles.modalButton}
                onClick={() => setNotification(null)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <TodoCompletionModal
        isOpen={notification === 'all_complete'}
        onClose={() => setNotification(null)}
        onSetNewGoal={() => {
          setNotification(null);
          onBackToChat('mission_complete');
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalContent}>
              <div className={styles.modalIcon}>
                <WarningIcon size={48} />
              </div>
              <h2 className={styles.modalTitle}>
                Delete {deleteConfirm.type === 'todo' ? 'Task' : 'Stage'}?
              </h2>
              <p className={styles.modalText}>
                Are you sure you want to delete "{deleteConfirm.name}"?
                {deleteConfirm.type === 'stage' && ' This will delete all tasks in this stage.'}
                <br />
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button
                  className={styles.modalButtonDanger}
                  onClick={() => {
                    if (deleteConfirm.type === 'todo') {
                      handleDeleteTodo(deleteConfirm.id);
                    } else {
                      handleDeleteStage(deleteConfirm.id);
                    }
                  }}
                >
                  Delete
                </button>
                <button
                  className={styles.modalButtonSecondary}
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.stagesList}>
        {stages.map((stage) => {
          const stageNumber = stage.tier_number;
          const progress = getStageProgress(stage);
          const completed = isCompleted(stageNumber);
          const isCurrent = stageNumber === currentStage;
          const isExpanded = expandedStages.has(stageNumber);

          return (
            <div
              key={stageNumber}
              className={`${styles.stage} ${
                completed ? styles.completed : ''
              } ${isCurrent ? styles.current : ''}`}
            >
              <div
                className={styles.stageHeader}
                onClick={() => editing.type !== 'stage' && toggleStageExpanded(stageNumber)}
              >
                <div className={styles.stageHeaderLeft}>
                  <span className={styles.stageNumber}>STAGE {stageNumber}</span>
                  {editing.type === 'stage' && editing.id === stageNumber ? (
                    <input
                      type="text"
                      className={styles.inlineInput}
                      value={editing.text}
                      onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveStageEdit();
                        if (e.key === 'Escape') handleCancelStageEdit();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <span className={styles.stageTitle}>{stage.tier_title}</span>
                  )}
                </div>
                <span className={`${styles.accordionIcon} ${isExpanded ? styles.expanded : ''}`}>
                  ▼
                </span>
                <div className={styles.stageHeaderRight} onClick={(e) => e.stopPropagation()}>
                  {editing.type === 'stage' && editing.id === stageNumber ? (
                    <div className={styles.editActions}>
                      <button
                        className={styles.saveButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveStageEdit();
                        }}
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        className={styles.deleteButtonInline}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ type: 'stage', id: stageNumber, name: stage.tier_title });
                        }}
                        title="Delete stage"
                      >
                        ×
                      </button>
                      <button
                        className={styles.cancelButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelStageEdit();
                        }}
                        title="Cancel"
                      >
                        ↩
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.progressBadge}>
                        {progress.completed}/{progress.total} complete{' '}
                        {progress.completed === progress.total ? '✓' : ''}
                      </span>
                      <button
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStage(stageNumber, stage.tier_title);
                        }}
                        title="Edit"
                      >
                        ···
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className={styles.todosList}>
                  {stage.todos
                    .filter(todo => !todo.is_deleted)
                    .map((todo) => (
                      <Todo
                        key={todo.id}
                        todoId={todo.id}
                        todoText={todo.todo_text}
                        isCompleted={todo.is_completed}
                        isOptional={todo.is_optional}
                        variant="list"
                        onToggle={handleToggleTodo}
                        showActions={true}
                        onEdit={() => handleEditTodo(todo.id, todo.todo_text)}
                        onDelete={() => setDeleteConfirm({ type: 'todo', id: todo.id, name: todo.todo_text })}
                        isEditing={editing.type === 'todo' && editing.id === todo.id}
                        editValue={editing.text}
                        onEditChange={(newText) => setEditing({ ...editing, text: newText })}
                        onEditSave={handleSaveTodoEdit}
                        onEditCancel={handleCancelTodoEdit}
                      />
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TodoList;
